import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { BlockMath } from "react-katex";
import { Layer, Line, Stage } from "react-konva";
import ConfirmDialog from "../components/ConfirmDialog";
import FigureAssistPanel from "../components/FigureAssistPanel";
import { FigureRender } from "../components/FigureRender";
import { extractFigureAssistLabelHints } from "../lib/figureLabelHints";
import "katex/dist/katex.min.css";

const difficulties = ["LOT", "MOT", "HOT"];

function parseJson(value, fallback = {}) {
  try {
    return { ok: true, data: value ? JSON.parse(value) : fallback };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

function getEditableFrameSchema(frameSchema) {
  if (!frameSchema || typeof frameSchema !== "object" || Array.isArray(frameSchema)) return {};
  return frameSchema;
}

function getFramePresentation(frameSchema, options = {}) {
  const schema = getEditableFrameSchema(frameSchema);
  const presentation =
    schema.presentation && typeof schema.presentation === "object" && !Array.isArray(schema.presentation)
      ? schema.presentation
      : {};
  return {
    showQuestionFigure:
      typeof presentation.showQuestionFigure === "boolean"
        ? presentation.showQuestionFigure
        : Boolean(options.hasFigure),
    showStudentFigurePanel:
      typeof presentation.showStudentFigurePanel === "boolean"
        ? presentation.showStudentFigurePanel
        : Boolean(options.isGeometry)
  };
}

function updateFramePresentationSchema(frameSchema, patch) {
  const schema = { ...getEditableFrameSchema(frameSchema) };
  const presentation =
    schema.presentation && typeof schema.presentation === "object" && !Array.isArray(schema.presentation)
      ? schema.presentation
      : {};
  schema.presentation = { ...presentation, ...patch };
  return schema;
}

function extractSchemaVariables(schema) {
  if (!schema || typeof schema !== "object") return {};
  if (schema.variables && typeof schema.variables === "object") {
    return schema.variables;
  }
  return Object.keys(schema).reduce((acc, key) => {
    if (!["constraints", "termPool", "termPools", "derived", "computed"].includes(key)) acc[key] = schema[key];
    return acc;
  }, {});
}

function extractDerivedDefinitions(schema) {
  if (!schema || typeof schema !== "object") return {};
  if (schema.derived && typeof schema.derived === "object") return schema.derived;
  if (schema.computed && typeof schema.computed === "object") return schema.computed;
  return {};
}

function pickSampleValue(definition) {
  if (Array.isArray(definition)) {
    if (!definition.length) return 1;
    return definition[0];
  }
  if (definition && typeof definition === "object") {
    if (Array.isArray(definition.set) && definition.set.length) return definition.set[0];
    if (typeof definition.min === "number" && typeof definition.max === "number") {
      return definition.min;
    }
  }
  if (typeof definition === "number") return definition;
  return 1;
}

function pickPositiveInteger(value, fallback = 2) {
  const num = Number(value);
  if (Number.isFinite(num) && num > 0) return Math.floor(num);
  return fallback;
}

function buildSampleVariables(schema) {
  const extracted = extractSchemaVariables(schema);
  return Object.entries(extracted).reduce((acc, [key, value]) => {
    acc[key] = pickSampleValue(value);
    return acc;
  }, {});
}

function buildSingleTermPoolPreview(termPool, currentVars) {
  const terms = Array.isArray(termPool.terms)
    ? termPool.terms.filter((term) => term && typeof term === "object" && term.latex)
    : [];
  if (!terms.length) return currentVars;

  let k = 0;
  if (termPool.kFrom && Object.prototype.hasOwnProperty.call(currentVars, termPool.kFrom)) {
    k = pickPositiveInteger(currentVars[termPool.kFrom], 0);
  } else if (Object.prototype.hasOwnProperty.call(termPool, "k")) {
    k = pickPositiveInteger(pickSampleValue(termPool.k), 0);
  }
  if (!k) k = Math.min(2, terms.length);

  const distinctByFamily =
    termPool?.rules?.noDuplicateFamilies === true ||
    String(termPool?.distinctBy || "").toLowerCase() === "family";

  const selected = [];
  const seenFamily = new Set();
  for (const term of terms) {
    if (selected.length >= k) break;
    const family = String(term.family || "").trim();
    if (distinctByFamily && family) {
      if (seenFamily.has(family)) continue;
      seenFamily.add(family);
    }
    selected.push(term);
  }
  if (!selected.length) return currentVars;

  const renderedTerms = selected.map((term) => {
    const localDefs = term.variables && typeof term.variables === "object" ? term.variables : {};
    const localVars = Object.entries(localDefs).reduce((acc, [key, value]) => {
      acc[key] = pickSampleValue(value);
      return acc;
    }, {});
    return renderTemplate(term.latex, { ...currentVars, ...localVars });
  });

  const outputVar = String(termPool.outputVar || "expr").trim() || "expr";
  const joinWith = String(termPool.joinWith || " + ");
  return { ...currentVars, [outputVar]: renderedTerms.join(joinWith) };
}

function buildTermPoolPreviewVariables(schema, baseVariables) {
  if (!schema || typeof schema !== "object") return { ...baseVariables };

  // Support termPools[] array (plural) — process each pool in sequence.
  // Fall back to singular termPool for backward compat.
  const poolList = Array.isArray(schema.termPools) && schema.termPools.length
    ? schema.termPools.filter((p) => p && typeof p === "object")
    : schema.termPool && typeof schema.termPool === "object"
    ? [schema.termPool]
    : [];

  let acc = { ...baseVariables };
  for (const pool of poolList) {
    acc = buildSingleTermPoolPreview(pool, acc);
  }
  return acc;
}

function gcd(a, b) {
  let x = Math.abs(Number(a) || 0);
  let y = Math.abs(Number(b) || 0);
  while (y) {
    const temp = y;
    y = x % y;
    x = temp;
  }
  return x;
}

function lcm(a, b) {
  const x = Math.abs(Number(a) || 0);
  const y = Math.abs(Number(b) || 0);
  if (!x || !y) return 0;
  return (x * y) / gcd(x, y);
}

function evaluateDerivedExpression(expr, scope) {
  const keys = Object.keys(scope || {});
  const values = keys.map((key) => scope[key]);
  const compiled = String(expr || "")
    .replace(/\^/g, "**")
    .replace(/\bmod\s*\(/g, "mod(");
  // eslint-disable-next-line no-new-func
  const fn = new Function(
    ...keys,
    "abs",
    "ceil",
    "floor",
    "gcd",
    "lcm",
    "max",
    "min",
    "mod",
    "pow",
    "round",
    "sqrt",
    `"use strict"; return (${compiled});`
  );
  return fn(
    ...values,
    Math.abs,
    Math.ceil,
    Math.floor,
    gcd,
    lcm,
    Math.max,
    Math.min,
    (a, b) => Number(a) % Number(b),
    Math.pow,
    Math.round,
    Math.sqrt
  );
}

function applyDerivedPreviewValueOptions(value, definition) {
  if (typeof value !== "number" || !Number.isFinite(value)) return value;
  if (!definition || typeof definition !== "object") return value;
  if (typeof definition.round === "number") {
    const factor = Math.pow(10, Math.max(0, Number(definition.round) || 0));
    return Math.round(value * factor) / factor;
  }
  if (definition.integer === true) return Math.round(value);
  if (definition.floor === true) return Math.floor(value);
  if (definition.ceil === true) return Math.ceil(value);
  return value;
}

function resolveDerivedPreviewVariables(schema, baseVariables) {
  const derived = extractDerivedDefinitions(schema);
  const pending = new Map(Object.entries(derived).filter(([key]) => String(key || "").trim()));
  const resolved = { ...baseVariables };
  if (!pending.size) return resolved;

  for (let pass = 0; pass < pending.size + 2; pass += 1) {
    let progressed = false;
    for (const [key, definition] of [...pending.entries()]) {
      if (definition === null || definition === undefined) {
        resolved[key] = definition;
        pending.delete(key);
        progressed = true;
        continue;
      }
      if (typeof definition === "number" || typeof definition === "boolean") {
        resolved[key] = definition;
        pending.delete(key);
        progressed = true;
        continue;
      }

      const expr =
        typeof definition === "string"
          ? definition
          : typeof definition?.expr === "string"
          ? definition.expr
          : "";
      if (!expr.trim()) continue;

      try {
        const value = evaluateDerivedExpression(expr, resolved);
        const normalized =
          typeof value === "number" ? applyDerivedPreviewValueOptions(value, definition) : value;
        if (normalized === undefined) continue;
        resolved[key] = normalized;
        pending.delete(key);
        progressed = true;
      } catch (err) {
        continue;
      }
    }
    if (!pending.size || !progressed) break;
  }

  return resolved;
}

function buildPreviewVariables(schema) {
  const base = buildSampleVariables(schema);
  const withTermPool = buildTermPoolPreviewVariables(schema, base);
  return resolveDerivedPreviewVariables(schema, withTermPool);
}

function normalizeLatex(input) {
  if (!input) return "";
  let output = String(input);
  output = output.replace(/\+\s*-/g, " - ");
  output = output.replace(/-\s*-/g, " + ");
  // Universal zero-coefficient removal: works for any variable/function name,
  // with or without LaTeX backslash, in all term positions.
  // After +/- operator — remove the operator and the entire zero term
  output = output.replace(/\s*[+-]\s*0\s*\*?\s*(?:[a-zA-Z\\][^+=()[\]-]*)/g, "");
  // At start of string — remove leading zero term
  output = output.replace(/^0\s*\*?\s*(?:[a-zA-Z\\][^+=()[\]-]*)/g, "");
  // After ( or = when another term follows — e.g. "(0sin + cos)" → "(cos)"
  output = output.replace(/([(=])\s*0\s*\*?\s*(?:[a-zA-Z\\][^+=()[\]-]*)(?=\s*[+-])/g, "$1");
  // Clean up structural artifacts left by removal
  output = output.replace(/\(\s*\+\s*/g, "(");
  output = output.replace(/=\s*\+\s*/g, "= ");
  output = output.replace(/^\s*\+\s*/, "");
  output = output.replace(/\s{2,}/g, " ");
  output = output.replace(/(^|[=+(\s-])1\s*\*?\s*(?=(\\[a-zA-Z]+|[a-zA-Z(]))/g, "$1");
  output = output.replace(/(^|[=+(\s])-\s*1\s*\*?\s*(?=(\\[a-zA-Z]+|[a-zA-Z(]))/g, "$1-");
  output = output.replace(/\b1\s*x\b/g, "x");
  output = output.replace(/-1x\b/g, "-x");
  output = output.replace(/\^\s*\{\s*\+?1(?:\.0+)?\s*\}/g, "");
  output = output.replace(/\^\s*\(\s*\+?1(?:\.0+)?\s*\)/g, "");
  output = output.replace(/([a-zA-Z0-9)\]])\s*\^\s*\+?1\b/g, "$1");
  output = output.replace(/\+\s*-\s*(\d+)/g, "- $1");
  output = output.replace(/=\s*-\s*(\d+)/g, "= -$1");
  return output;
}

function renderTemplate(input, variables) {
  const substituted = String(input || "").replace(/\{\{([a-zA-Z0-9_]+)\}\}|\{([a-zA-Z0-9_]+)\}/g, (match, d, s) => {
    const key = d || s;
    if (Object.prototype.hasOwnProperty.call(variables || {}, key)) {
      return String(variables[key]);
    }
    return match;
  });
  return normalizeLatex(substituted);
}

function AdminFrameEditor({ apiBase, token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { frameId } = useParams();
  const isCreate = !frameId;
  const [chapters, setChapters] = useState([]);
  const [notice, setNotice] = useState({ type: "", text: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showClearAssistConfirm, setShowClearAssistConfirm] = useState(false);
  const [assistLoading, setAssistLoading] = useState(false);
  const [assistError, setAssistError] = useState("");
  const [assistResult, setAssistResult] = useState({
    latexTemplate: "",
    renderedLatex: "",
    notes: ""
  });
  const [assistLines, setAssistLines] = useState([]);
  const [assistDrawing, setAssistDrawing] = useState(false);
  const [assistStrokeColor, setAssistStrokeColor] = useState("#0f172a");
  const [assistStageSize, setAssistStageSize] = useState({ width: 740, height: 260 });
  const [figurePreviewDataUrl, setFigurePreviewDataUrl] = useState("");
  const [figureEditorState, setFigureEditorState] = useState(null);
  const [validatingAnalysis, setValidatingAnalysis] = useState(false);
  const [analysisValidation, setAnalysisValidation] = useState(null);
  const assistContainerRef = useRef(null);
  const assistStageRef = useRef(null);
  const [form, setForm] = useState({
    chapterId: "",
    title: "",
    frameOrder: 1,
    difficulty: "LOT",
    isLibraryFrame: location.state?.isLibraryFrame === true,
    latexTemplate: "",
    variablesSchema: location.state?.isLibraryFrame ? "{\n  \"inputs\": [],\n  \"outputs\": []\n}" : "{}",
    frameSchema: "",
    figureSpec: "",
    diagramSchema: "",
    tags: "",
    active: true
  });

  const parsedVars = useMemo(() => parseJson(form.variablesSchema, {}), [form.variablesSchema]);
  const parsedFrameSchema = useMemo(() => parseJson(form.frameSchema, null), [form.frameSchema]);
  const parsedFigure = useMemo(() => parseJson(form.figureSpec, null), [form.figureSpec]);
  const parsedDiagram = useMemo(() => parseJson(form.diagramSchema, null), [form.diagramSchema]);
  const figureAssistLabelHints = useMemo(
    () =>
      extractFigureAssistLabelHints({
        figureSpec: parsedFigure.ok ? parsedFigure.data : null,
        diagramSchema: parsedDiagram.ok ? parsedDiagram.data : null
      }),
    [parsedDiagram, parsedFigure]
  );
  const framePresentation = useMemo(
    () =>
      getFramePresentation(parsedFrameSchema.ok ? parsedFrameSchema.data : null, {
        hasFigure: Boolean(parsedFigure.ok && parsedFigure.data),
        isGeometry: form.chapterId === "geometry"
      }),
    [form.chapterId, parsedFigure, parsedFrameSchema]
  );
  const presentationControlsDisabled = !parsedFrameSchema.ok && Boolean(form.frameSchema.trim());
  const templatePreviewLatex = useMemo(() => {
    if (!form.latexTemplate) return "";
    if (!parsedVars.ok) return form.latexTemplate;
    const sampleVariables = buildPreviewVariables(parsedVars.data);
    if (!Object.keys(sampleVariables).length) return form.latexTemplate;
    return renderTemplate(form.latexTemplate, sampleVariables);
  }, [form.latexTemplate, parsedVars]);

  useEffect(() => {
    const selected = location.state?.frame;
    if (isCreate || !selected || selected._id !== frameId) return;
    setForm({
      chapterId: selected.chapterId || "",
      title: selected.title || "",
      frameOrder: Number(selected.frameOrder) || 1,
      difficulty: selected.difficulty || "LOT",
      isLibraryFrame: selected.isLibraryFrame === true,
      latexTemplate: selected.latexTemplate || "",
      variablesSchema: JSON.stringify(selected.variablesSchema || {}, null, 2),
      frameSchema: selected.frameSchema ? JSON.stringify(selected.frameSchema, null, 2) : "",
      figureSpec: selected.figureSpec ? JSON.stringify(selected.figureSpec, null, 2) : "",
      diagramSchema: selected.diagramSchema ? JSON.stringify(selected.diagramSchema, null, 2) : "",
      tags: Array.isArray(selected.tags) ? selected.tags.join(", ") : "",
      active: selected.active !== false
    });
    setFigureEditorState(selected.figureEditorState || null);
    setFigurePreviewDataUrl(String(selected.figurePreviewDataUrl || "").trim());
  }, [location.state, isCreate, frameId]);

  useEffect(() => {
    setAnalysisValidation(null);
  }, [form.chapterId, form.title, form.difficulty, form.latexTemplate, form.variablesSchema, form.frameSchema, form.diagramSchema]);

  useEffect(() => {
    function updateStageSize() {
      if (!assistContainerRef.current) return;
      const style = window.getComputedStyle(assistContainerRef.current);
      const paddingLeft = parseFloat(style.paddingLeft || "0");
      const paddingRight = parseFloat(style.paddingRight || "0");
      const width = Math.max(320, Math.floor(assistContainerRef.current.clientWidth - paddingLeft - paddingRight));
      setAssistStageSize((prev) =>
        prev.width === width ? prev : { ...prev, width }
      );
    }

    updateStageSize();
    window.addEventListener("resize", updateStageSize);
    let observer = null;
    if (typeof ResizeObserver !== "undefined" && assistContainerRef.current) {
      observer = new ResizeObserver(() => updateStageSize());
      observer.observe(assistContainerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateStageSize);
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadData() {
      setLoading(true);
      try {
        const [framesRes, chaptersRes] = await Promise.all([
          fetch(`${apiBase}/api/admin/frames`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiBase}/api/admin/chapters`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (!framesRes.ok || !chaptersRes.ok) throw new Error("Failed to load frame data");
        const [framesPayload, chaptersPayload] = await Promise.all([framesRes.json(), chaptersRes.json()]);
        const chapterItems = chaptersPayload.chapters || [];
        if (!mounted) return;
        setChapters(chapterItems);
        if (isCreate) {
          setForm((prev) => ({ ...prev, chapterId: prev.chapterId || chapterItems[0]?.chapterId || "" }));
          return;
        }
        const target = (framesPayload.frames || []).find((item) => item._id === frameId);
        if (!target) throw new Error("Frame not found");
        setForm({
          chapterId: target.chapterId || "",
          title: target.title || "",
          frameOrder: Number(target.frameOrder) || 1,
          difficulty: target.difficulty || "LOT",
          isLibraryFrame: target.isLibraryFrame === true,
          latexTemplate: target.latexTemplate || "",
          variablesSchema: JSON.stringify(target.variablesSchema || {}, null, 2),
          frameSchema: target.frameSchema ? JSON.stringify(target.frameSchema, null, 2) : "",
          figureSpec: target.figureSpec ? JSON.stringify(target.figureSpec, null, 2) : "",
          diagramSchema: target.diagramSchema ? JSON.stringify(target.diagramSchema, null, 2) : "",
          tags: Array.isArray(target.tags) ? target.tags.join(", ") : "",
          active: target.active !== false
        });
        setFigureEditorState(target.figureEditorState || null);
        setFigurePreviewDataUrl(String(target.figurePreviewDataUrl || "").trim());
      } catch (err) {
        if (mounted) setNotice({ type: "error", text: err.message || "Failed to load frame" });
      } finally {
        if (mounted) setLoading(false);
      }
    }
    if (token) loadData();
    return () => {
      mounted = false;
    };
  }, [apiBase, token, frameId, isCreate]);

  async function saveFrame() {
    if (!form.chapterId || !form.title) {
      setNotice({ type: "error", text: "Chapter and title are required." });
      return;
    }
    if (!form.isLibraryFrame && !form.latexTemplate) {
      setNotice({ type: "error", text: "LaTeX template is required for regular frames." });
      return;
    }
    if (!parsedVars.ok) {
      setNotice({ type: "error", text: `Variables schema JSON error: ${parsedVars.error}` });
      return;
    }
    if (!parsedFrameSchema.ok) {
      setNotice({ type: "error", text: `Frame schema JSON error: ${parsedFrameSchema.error}` });
      return;
    }
    if (!parsedFigure.ok) {
      setNotice({ type: "error", text: `Figure spec JSON error: ${parsedFigure.error}` });
      return;
    }
    if (!parsedDiagram.ok) {
      setNotice({ type: "error", text: `Diagram schema JSON error: ${parsedDiagram.error}` });
      return;
    }
    setSaving(true);
    setNotice({ type: "", text: "" });
    try {
      const payload = {
        chapterId: form.chapterId,
        title: form.title,
        frameOrder: Number(form.frameOrder) || 1,
        difficulty: form.difficulty,
        isLibraryFrame: form.isLibraryFrame === true,
        latexTemplate: form.latexTemplate,
        variablesSchema: parsedVars.data || {},
        frameSchema: parsedFrameSchema.data || null,
        figureSpec: parsedFigure.data || null,
        figureEditorState: figureEditorState || null,
        figurePreviewDataUrl: String(figurePreviewDataUrl || "").trim(),
        diagramSchema: parsedDiagram.data || null,
        tags: form.tags.split(",").map((x) => x.trim()).filter(Boolean),
        active: !!form.active
      };
      const endpoint = isCreate ? `${apiBase}/api/admin/frames` : `${apiBase}/api/admin/frames/${frameId}`;
      const method = isCreate ? "POST" : "PUT";
      const response = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to save frame");
      }
      const result = await response.json();
      if (isCreate) {
        navigate(`/admin/frames/${result.frame._id}`);
      } else {
        if (result?.frame) {
          setForm((prev) => ({
            ...prev,
            chapterId: result.frame.chapterId || prev.chapterId,
            difficulty: result.frame.difficulty || prev.difficulty,
            title: result.frame.title || prev.title,
            frameOrder: Number(result.frame.frameOrder) || prev.frameOrder,
            latexTemplate: result.frame.latexTemplate || prev.latexTemplate,
            variablesSchema: JSON.stringify(result.frame.variablesSchema || {}, null, 2),
            frameSchema: result.frame.frameSchema ? JSON.stringify(result.frame.frameSchema, null, 2) : "",
            figureSpec: result.frame.figureSpec ? JSON.stringify(result.frame.figureSpec, null, 2) : "",
            diagramSchema: result.frame.diagramSchema ? JSON.stringify(result.frame.diagramSchema, null, 2) : "",
            tags: Array.isArray(result.frame.tags) ? result.frame.tags.join(", ") : prev.tags,
            active: result.frame.active !== false
          }));
          setFigureEditorState(result.frame.figureEditorState || null);
          setFigurePreviewDataUrl(String(result.frame.figurePreviewDataUrl || "").trim());
        }
        setNotice({ type: "success", text: "Frame updated." });
      }
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to save frame" });
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive() {
    if (isCreate) return;
    setSaving(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/frames/${frameId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ active: !form.active })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update frame status");
      }
      setForm((prev) => ({ ...prev, active: !prev.active }));
      setNotice({ type: "success", text: `Frame set ${form.active ? "inactive" : "active"}.` });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to update frame status" });
    } finally {
      setSaving(false);
    }
  }

  async function removeFrame() {
    if (isCreate) return;
    setSaving(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/frames/${frameId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to delete frame");
      }
      navigate("/admin/frames");
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to delete frame" });
    } finally {
      setSaving(false);
    }
  }

  function handleAssistPointerDown(event) {
    event.evt.preventDefault();
    setAssistDrawing(true);
    const point = event.target.getStage().getPointerPosition();
    if (!point) return;
    setAssistLines((prev) => [...prev, { color: assistStrokeColor, points: [point.x, point.y] }]);
  }

  function handleAssistPointerMove(event) {
    if (!assistDrawing) return;
    const point = event.target.getStage().getPointerPosition();
    if (!point) return;
    setAssistLines((prev) => {
      const last = prev[prev.length - 1];
      if (!last) return prev;
      const updated = { ...last, points: [...last.points, point.x, point.y] };
      return [...prev.slice(0, -1), updated];
    });
  }

  function handleAssistPointerUp() {
    setAssistDrawing(false);
  }

  function clearAssistSlate() {
    setAssistLines([]);
  }

  function undoAssistStroke() {
    setAssistLines((prev) => prev.slice(0, -1));
  }

  async function runLatexAssistFromSlate() {
    if (!assistStageRef.current || !assistLines.length) {
      setAssistError("Draw the frame first on the slate.");
      return;
    }

    if (!parsedVars.ok) {
      setAssistError(`Fix variables schema JSON first: ${parsedVars.error}`);
      return;
    }

    setAssistLoading(true);
    setAssistError("");
    setNotice({ type: "", text: "" });

    try {
      const dataUrl = assistStageRef.current.toDataURL({ pixelRatio: 2 });
      const response = await fetch(`${apiBase}/api/admin/frames/latex-assist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          dataUrl,
          variablesSchema: parsedVars.data || {}
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Failed to convert handwritten frame.");
      }

      const suggestion = payload.suggestion || {};
      setAssistResult({
        latexTemplate: suggestion.latexTemplate || "",
        renderedLatex: suggestion.renderedLatex || "",
        notes: suggestion.notes || ""
      });
      setNotice({
        type: "success",
        text: "Handwritten frame converted. Review template and apply if correct."
      });
    } catch (err) {
      setAssistError(err.message || "Failed to convert handwritten frame.");
    } finally {
      setAssistLoading(false);
    }
  }

  function applyAssistTemplate() {
    if (!assistResult.latexTemplate) return;
    setForm((prev) => ({ ...prev, latexTemplate: assistResult.latexTemplate }));
    setNotice({ type: "success", text: "Suggested template inserted into LaTeX Template." });
  }

  async function acceptFigureAssist(result) {
    setForm((prev) => ({
      ...prev,
      figureSpec: result?.figureSpec ? JSON.stringify(result.figureSpec, null, 2) : "",
      diagramSchema: result?.diagramSchema ? JSON.stringify(result.diagramSchema, null, 2) : ""
    }));
    setFigureEditorState(result?.editorState || null);
    setFigurePreviewDataUrl(String(result?.dataUrl || "").trim());
    setNotice({
      type: "success",
      text: "Captured figure inserted into Figure Spec and Diagram Schema."
    });
    return { ...result, status: "accepted" };
  }

  function handlePresentationToggle(key, checked) {
    const parsedSchema = parseJson(form.frameSchema, null);
    if (!parsedSchema.ok) {
      setNotice({ type: "error", text: `Frame schema JSON error: ${parsedSchema.error}` });
      return;
    }
    const nextSchema = updateFramePresentationSchema(parsedSchema.data, {
      [key]: key === "showStudentFigurePanel" && form.chapterId !== "geometry" ? false : !!checked
    });
    setForm((prev) => ({
      ...prev,
      frameSchema: JSON.stringify(nextSchema, null, 2)
    }));
  }

  async function validateDeterministicAnalysis() {
    if (!parsedVars.ok) {
      setNotice({ type: "error", text: `Variables schema JSON error: ${parsedVars.error}` });
      return;
    }
    if (!parsedFrameSchema.ok) {
      setNotice({ type: "error", text: `Frame schema JSON error: ${parsedFrameSchema.error}` });
      return;
    }
    if (!parsedDiagram.ok) {
      setNotice({ type: "error", text: `Diagram schema JSON error: ${parsedDiagram.error}` });
      return;
    }

    setValidatingAnalysis(true);
    setAnalysisValidation(null);
    setNotice({ type: "", text: "" });
    try {
      const response = await fetch(`${apiBase}/api/admin/frames/validate-analysis`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          chapterId: form.chapterId,
          title: form.title,
          difficulty: form.difficulty,
          latexTemplate: form.latexTemplate,
          variablesSchema: parsedVars.data || {},
          frameSchema: parsedFrameSchema.data || null,
          diagramSchema: parsedDiagram.data || null
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.details?.message || payload?.error || "Failed to validate deterministic analysis");
      }
      setAnalysisValidation(payload.validation || null);
      setNotice({
        type: payload?.validation?.applicable ? "success" : "error",
        text: payload?.validation?.applicable
          ? "Deterministic analysis is available for this frame draft."
          : payload?.validation?.reason || "Deterministic analysis is not available for this frame draft."
      });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to validate deterministic analysis" });
    } finally {
      setValidatingAnalysis(false);
    }
  }

  return (
    <div className="admin-panel">
      <div className="admin-page-header">
        <Link className="secondary-button" to="/admin/frames">
          Back
        </Link>
        <div className="analysis-label">{isCreate ? "Create Frame" : "Update Frame"}</div>
      </div>
      {notice.text && (
        <div className={`admin-notice ${notice.type === "error" ? "error" : "success"}`}>{notice.text}</div>
      )}
      <div className="admin-editor">
        {loading && <div className="analysis-note">Loading frame...</div>}
        <label>Chapter</label>
        <select value={form.chapterId} onChange={(event) => setForm((prev) => ({ ...prev, chapterId: event.target.value }))}>
          {chapters.map((chapter) => (
            <option key={chapter.chapterId} value={chapter.chapterId}>
              {chapter.title} - {chapter.grade || "Grade 9"} ({chapter.chapterId})
            </option>
          ))}
        </select>
        <label>Frame Title</label>
        <input value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} />
        {!form.isLibraryFrame && (
          <>
            <label>Difficulty</label>
            <div className="level-toggle">
              {difficulties.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={`toggle-button ${form.difficulty === item ? "active" : ""}`}
                  onClick={() => setForm((prev) => ({ ...prev, difficulty: item }))}
                >
                  {item}
                </button>
              ))}
            </div>
            <label>Frame Order</label>
            <input
              type="number"
              value={form.frameOrder}
              onChange={(event) => setForm((prev) => ({ ...prev, frameOrder: Number(event.target.value) || 1 }))}
            />
          </>
        )}
        {form.isLibraryFrame && (
          <div className="admin-notice" style={{ background: "rgba(124,58,237,0.07)", borderColor: "#a78bfa", color: "#5b21b6", marginBottom: 4 }}>
            <strong>Library Frame</strong> — no LaTeX template needed. Define <code>inputs</code> and <code>outputs</code> in the variable schema below.
          </div>
        )}
        {!form.isLibraryFrame && <label>LaTeX Template</label>}
        {!form.isLibraryFrame && <textarea rows={4} value={form.latexTemplate} onChange={(event) => setForm((prev) => ({ ...prev, latexTemplate: event.target.value }))} />}
        {templatePreviewLatex && (
          <details open>
            <summary>Rendered Template Preview</summary>
            <BlockMath math={templatePreviewLatex} />
          </details>
        )}
        <details open>
          <summary>Write Frame on Slate (Auto LaTeX Assist)</summary>
          <div className="question-meta">
            Draw the full question frame here. Click convert to get a suggested LaTeX template and math preview.
          </div>
          <div className="admin-slate-assist">
            <div className="admin-slate-toolbar">
              <button
                className={`tool-chip ${assistStrokeColor === "#0f172a" ? "active" : ""}`}
                type="button"
                onClick={() => setAssistStrokeColor("#0f172a")}
              >
                Black
              </button>
              <button
                className={`tool-chip ${assistStrokeColor === "#1d4ed8" ? "active" : ""}`}
                type="button"
                onClick={() => setAssistStrokeColor("#1d4ed8")}
              >
                Blue
              </button>
            </div>
            <div className="admin-slate-stage" ref={assistContainerRef}>
              <Stage
                ref={assistStageRef}
                width={assistStageSize.width}
                height={assistStageSize.height}
                onPointerDown={handleAssistPointerDown}
                onPointerMove={handleAssistPointerMove}
                onPointerUp={handleAssistPointerUp}
              >
                <Layer>
                  {assistLines.map((line, index) => (
                    <Line
                      key={`assist-line-${index}`}
                      points={line.points}
                      stroke={line.color}
                      strokeWidth={2.5}
                      tension={0.45}
                      lineCap="round"
                      lineJoin="round"
                    />
                  ))}
                </Layer>
              </Stage>
            </div>
            <div className="admin-actions">
              <button className="secondary-button" type="button" onClick={undoAssistStroke}>
                Undo Stroke
              </button>
              <button className="secondary-button" type="button" onClick={() => setShowClearAssistConfirm(true)}>
                Clear Slate
              </button>
              <button
                className="primary-button"
                type="button"
                onClick={runLatexAssistFromSlate}
                disabled={assistLoading}
              >
                {assistLoading ? "Converting..." : "Convert to LaTeX Template"}
              </button>
            </div>
            {assistError && <div className="error-text">{assistError}</div>}
            {assistResult.latexTemplate && (
              <div className="analysis-grid">
                <label>Suggested LaTeX Template</label>
                <textarea
                  rows={3}
                  value={assistResult.latexTemplate}
                  onChange={(event) =>
                    setAssistResult((prev) => ({ ...prev, latexTemplate: event.target.value }))
                  }
                />
                <div className="admin-actions">
                  <button className="secondary-button" type="button" onClick={applyAssistTemplate}>
                    Use Suggested Template
                  </button>
                </div>
                {assistResult.renderedLatex && (
                  <>
                    <div className="question-meta">Rendered Math Preview</div>
                    <BlockMath math={assistResult.renderedLatex} />
                  </>
                )}
                {assistResult.notes && <div className="question-meta">{assistResult.notes}</div>}
              </div>
            )}
          </div>
        </details>
        <label>Variables Schema (JSON)</label>
        <textarea rows={4} value={form.variablesSchema} onChange={(event) => setForm((prev) => ({ ...prev, variablesSchema: event.target.value }))} />
        <label>Frame Schema (JSON)</label>
        <textarea rows={4} value={form.frameSchema} onChange={(event) => setForm((prev) => ({ ...prev, frameSchema: event.target.value }))} />
        <div className="admin-checkbox-group">
          <div className="question-meta">Geometry Presentation</div>
          <label className="admin-checkbox-row">
            <input
              type="checkbox"
              checked={framePresentation.showQuestionFigure}
              disabled={presentationControlsDisabled}
              onChange={(event) => handlePresentationToggle("showQuestionFigure", event.target.checked)}
            />
            <span>Show the authored question figure to the student.</span>
          </label>
          <label className="admin-checkbox-row">
            <input
              type="checkbox"
              checked={framePresentation.showStudentFigurePanel}
              disabled={presentationControlsDisabled || form.chapterId !== "geometry"}
              onChange={(event) => handlePresentationToggle("showStudentFigurePanel", event.target.checked)}
            />
            <span>Show the student figure sketch panel in practice.</span>
          </label>
          <div className="question-meta">
            Use the first option for provided-diagram questions, the second for draw-the-figure questions, or both for hybrid geometry frames.
          </div>
          {presentationControlsDisabled && (
            <div className="error-text">Fix the Frame Schema JSON before editing the presentation checkboxes.</div>
          )}
        </div>
        <FigureAssistPanel
          key={`frame-figure-${frameId || "create"}`}
          title="Draw Figure"
          helperText="Draw the geometry figure. The panel snaps near-straight strokes into geometry primitives and shows the captured entities and dependencies before you accept it."
          resetKey={`${frameId || "create"}:${form.title}:${form.chapterId}`}
          suggestedPoints={figureAssistLabelHints.suggestedPoints}
          suggestedLabels={figureAssistLabelHints.suggestedLabels}
          enableValueLabels
          initialEditorState={figureEditorState}
          initialFigureSpec={parsedFigure.ok ? parsedFigure.data : null}
          initialAcceptedDataUrl={figurePreviewDataUrl}
          onAccept={acceptFigureAssist}
        />
        {(String(figurePreviewDataUrl || "").trim() || (parsedFigure.ok && parsedFigure.data)) && (
          <>
            <div className="question-meta">Saved Figure Preview</div>
            <div className="figure-block">
              {String(figurePreviewDataUrl || "").trim() ? (
                <img
                  className="attempt-figure-image"
                  src={String(figurePreviewDataUrl || "").trim()}
                  alt="Saved accepted figure preview"
                />
              ) : (
                <FigureRender figureSpec={parsedFigure.data} cropToContent contentPadding={8} theme="accepted" />
              )}
            </div>
          </>
        )}
        <label>Figure Spec (JSON)</label>
        <textarea rows={4} value={form.figureSpec} onChange={(event) => setForm((prev) => ({ ...prev, figureSpec: event.target.value }))} />
        <label>Diagram Schema (JSON)</label>
        <textarea rows={4} value={form.diagramSchema} onChange={(event) => setForm((prev) => ({ ...prev, diagramSchema: event.target.value }))} />
        <div className="admin-actions">
          <button
            className="secondary-button"
            type="button"
            onClick={validateDeterministicAnalysis}
            disabled={validatingAnalysis}
          >
            {validatingAnalysis ? "Validating..." : "Validate Analysis"}
          </button>
        </div>
        {analysisValidation && (
          <div className={`admin-validation-card ${analysisValidation.applicable ? "success" : "error"}`}>
            <div className="admin-validation-header">
              <div className="analysis-label">
                {analysisValidation.applicable ? "Deterministic Analysis Ready" : "Deterministic Analysis Not Ready"}
              </div>
              <div className="question-meta">
                {[
                  analysisValidation.engine ? `Engine: ${analysisValidation.engine}` : "",
                  analysisValidation.family ? `Family: ${analysisValidation.family}` : ""
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </div>
            </div>
            {analysisValidation.reason && <div className="error-text">{analysisValidation.reason}</div>}
            {analysisValidation.selectedCombinationId && (
              <div className="question-meta">
                Combination: {analysisValidation.selectedCombinationLabel || analysisValidation.selectedCombinationId}
              </div>
            )}
            {Array.isArray(analysisValidation.targets) && analysisValidation.targets.length > 0 && (
              <div className="question-meta">
                Targets:{" "}
                {analysisValidation.targets
                  .map((target) =>
                    target?.symbol && target?.value !== null && target?.value !== undefined
                      ? `${target.symbol} = ${target.value}`
                      : target?.symbol || ""
                  )
                  .filter(Boolean)
                  .join(", ")}
              </div>
            )}
            {analysisValidation.questionLatex && (
              <details open>
                <summary>Rendered Question Used For Validation</summary>
                <BlockMath math={analysisValidation.questionLatex} />
              </details>
            )}
            {analysisValidation.variables && Object.keys(analysisValidation.variables).length > 0 && (
              <details>
                <summary>Sample Variables</summary>
                <pre className="admin-validation-json">
                  {JSON.stringify(analysisValidation.variables, null, 2)}
                </pre>
              </details>
            )}
            {Array.isArray(analysisValidation.warnings) && analysisValidation.warnings.length > 0 && (
              <div className="admin-validation-warnings">
                {analysisValidation.warnings.map((warning, index) => (
                  <div key={`validation-warning-${index}`} className="question-meta">
                    Warning: {warning}
                  </div>
                ))}
              </div>
            )}
            {analysisValidation.applicable && Array.isArray(analysisValidation.canonicalSteps) && analysisValidation.canonicalSteps.length > 0 && (
              <details open>
                <summary>Deterministic Step Preview</summary>
                <div className="admin-validation-steps">
                  {analysisValidation.canonicalSteps.map((step) => (
                    <div key={`validation-step-${step.step}`} className="admin-validation-step">
                      <div className="question-meta">
                        Step {step.step} · {step.tag}
                        {step.keyStep ? " · key" : ""}
                      </div>
                      <BlockMath math={step.latex} />
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
        <label>Tags (comma separated)</label>
        <input value={form.tags} onChange={(event) => setForm((prev) => ({ ...prev, tags: event.target.value }))} />
        <div className="admin-actions">
          <button className="primary-button" type="button" onClick={saveFrame} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
          {!isCreate && (
            <>
              <button className="secondary-button" type="button" onClick={toggleActive} disabled={saving}>
                {form.active ? "Set Inactive" : "Set Active"}
              </button>
              <button className="danger-button" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
                Delete
              </button>
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={showClearAssistConfirm}
        title="Clear frame slate?"
        message="This will remove the handwritten assist slate content for this frame editor."
        confirmLabel="Clear Slate"
        busy={saving}
        onCancel={() => setShowClearAssistConfirm(false)}
        onConfirm={() => {
          setShowClearAssistConfirm(false);
          clearAssistSlate();
        }}
      />
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete frame?"
        message="This will permanently delete the question frame from the admin system."
        confirmLabel="Delete Frame"
        busy={saving}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setShowDeleteConfirm(false);
          await removeFrame();
        }}
      />
    </div>
  );
}

export default AdminFrameEditor;
